import React from "react";
import { AlertTriangle } from "lucide-react";
import type { InsightUnavailableMember } from "../../types/insightApi";

export interface StatusBannerProps {
	partial: boolean;
	unavailableMembers?: InsightUnavailableMember[];
}

const REASON_LABEL: Record<string, string> = {
	deleted: "source deleted",
	failed: "processing failed",
	missing: "no digest yet",
	unavailable: "temporarily unavailable",
	timeout: "timed out",
	unknown: "unknown state",
};

/**
 * Non-blocking partial/stale banner (CTX-97: "display partial/stale/deleted/
 * malformed-source status without blanking valid data"). Renders alongside
 * whatever topology/findings DID load successfully — it never replaces the
 * canvas or panel content, unlike the loading/error/empty states in
 * `DataStateView`, which are mutually exclusive with content.
 */
export const StatusBanner: React.FC<StatusBannerProps> = ({ partial, unavailableMembers = [] }) => {
	if (!partial) return null;

	return (
		<div
			data-testid="partial-status-banner"
			role="status"
			className="flex items-start gap-2 rounded-sm border border-semantic-border-warning-secondary bg-semantic-bg-warning-secondary px-3 py-2 text-sm text-semantic-text-warning"
		>
			<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
			<div className="flex flex-col gap-0.5">
				<span className="font-medium">Showing partial data</span>
				{unavailableMembers.length > 0 ? (
					<span className="text-xs">
						{unavailableMembers
							.map((member) => `${member.name} (${REASON_LABEL[member.reason] ?? member.reason})`)
							.join(", ")}
					</span>
				) : (
					<span className="text-xs">One or more contributing members did not complete cleanly.</span>
				)}
			</div>
		</div>
	);
};

export default StatusBanner;
